<template>
  <div class="auth-wrapper auth-v2">
    <b-row class="auth-inner m-0">

      <!-- Brand logo-->
      <b-link class="brand-logo">
        <img style="max-width: 140px;" src="@/assets/images/sarit_logo.png">
      </b-link>
      <!-- /Brand logo-->

      <!-- Left Text-->
      <b-col
        lg="8"
        :style="cssOptions"
        class="d-none d-lg-flex align-items-center p-5"
      >

      </b-col>
      <!-- /Left Text-->

      <!-- Login-->
      <b-col
        lg="4"
        class="d-flex align-items-center auth-bg px-2 p-lg-5"
      >
        <b-col
          sm="8"
          md="6"
          lg="12"
          class="px-xl-2 mx-auto"
        >
          <b-card-title
            class="mb-1 font-weight-bold"
            title-tag="h2"
          >
            Sarit Loyalty System
          </b-card-title>
          <b-card-text class="mb-2">
            {{ showOtpForm ? 'Please enter the OTP sent to you' : showOtpChannelSelection ? 'Select how you would like to receive your OTP' : 'Please enter your username and password' }}
          </b-card-text>

          <b-card-text class="mb-2">
            <div class="alert alert-danger p-3" v-if="errorMessage">
                  {{ errorMessage }}
            </div>
          </b-card-text>

          <!-- form -->
          <validation-observer
            ref="loginForm"
            #default="{invalid}"
          >
            <b-form
              class="auth-login-form mt-2"
              @submit.prevent="handleFormSubmit"
            >
              <!-- Step 1: Login Form - Email & Password -->
              <div v-if="!showOtpChannelSelection && !showOtpForm">
                <!-- email -->
                <b-form-group
                  label="Email"
                  label-for="login-email"
                >
                  <validation-provider
                    #default="{ errors }"
                    name="Email"
                    vid="email"
                    rules="required|email"
                  >
                    <b-form-input
                      id="login-email"
                      v-model="username"
                      :state="errors.length > 0 ? false:null"
                      name="login-email"
                      placeholder="john@example.com"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <!-- forgot password -->
                <b-form-group>
                  <div class="d-flex justify-content-between">
                    <label for="login-password">Password</label>
                    <b-link :to="{name:'auth-forgot-password'}">
                      <small>Forgot Password?</small>
                    </b-link>
                  </div>
                  <validation-provider
                    #default="{ errors }"
                    name="Password"
                    vid="password"
                    rules="required"
                  >
                    <b-input-group
                      class="input-group-merge"
                      :class="errors.length > 0 ? 'is-invalid':null"
                    >
                      <b-form-input
                        id="login-password"
                        v-model="password"
                        :state="errors.length > 0 ? false:null"
                        class="form-control-merge"
                        :type="passwordFieldType"
                        name="login-password"
                        placeholder="Password"
                      />
                      <b-input-group-append is-text>
                        <feather-icon
                          class="cursor-pointer"
                          :icon="passwordToggleIcon"
                          @click="togglePasswordVisibility"
                        />
                      </b-input-group-append>
                    </b-input-group>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </validation-provider>
                </b-form-group>

                <!-- checkbox -->
                <b-form-group>
                  <b-form-checkbox
                    id="remember-me"
                    v-model="status"
                    name="checkbox-1"
                  >
                    Remember Me
                  </b-form-checkbox>
                </b-form-group>

                <!-- submit buttons -->
                <b-button
                  type="submit"
                  variant="primary"
                  block
                  :disabled="invalid"
                >
                  Continue
                </b-button>
              </div>

              <!-- Step 2: OTP Channel Selection -->
              <div v-else-if="showOtpChannelSelection && !showOtpForm">
                <!-- OTP Channel Selection -->
                <b-form-group class="text-center">
                  <b-form-radio-group
                    v-model="otpChannel"
                    :options="otpChannelOptions"
                    name="otp-channel"
                    buttons
                    button-variant="outline-primary"
                    size="lg"
                    class="mt-3 mb-3"
                  ></b-form-radio-group>
                </b-form-group>

                <!-- Send OTP Button -->
                <b-button
                  variant="primary"
                  block
                  :disabled="isLoading"
                  @click="sendOTPCode"
                >
                  <b-spinner small v-if="isLoading" class="mr-1"></b-spinner>
                  {{ isLoading ? 'Sending OTP...' : 'Send OTP' }}
                </b-button>

                <!-- Back Button -->
                <b-button
                  variant="outline-secondary"
                  block
                  class="mt-2"
                  @click="backToCredentials"
                  :disabled="isLoading"
                >
                  Back
                </b-button>
              </div>

              <!-- Step 3: OTP Verification Form -->
              <div v-else-if="showOtpForm">
                <!-- OTP Input -->
                <b-form-group
                  class="text-center"
                >
                  <div class="d-flex justify-content-center mt-3 mb-4">
                    <CodeInput
                      :fields="6"
                      :loading="isLoading"
                      class="input"
                      v-on:change="onOtpChange"
                      v-on:complete="onOtpComplete"
                    />
                  </div>
                </b-form-group>

                <!-- Verify OTP Button -->
                <b-button
                  variant="primary"
                  block
                  :disabled="otpCode.length !== 6 || isLoading"
                  @click="verifyOTPCode"
                >
                  <b-spinner small v-if="isLoading" class="mr-1"></b-spinner>
                  {{ isLoading ? 'Verifying...' : 'Verify OTP' }}
                </b-button>

                <!-- Resend OTP -->
                <b-button
                  variant="link"
                  block
                  class="mt-2"
                  @click="resendOTP"
                  :disabled="isLoading"
                >
                  Resend OTP
                </b-button>

                <!-- Back Button -->
                <b-button
                  variant="outline-secondary"
                  block
                  class="mt-2"
                  @click="resetForm"
                  :disabled="isLoading"
                >
                  Back to Login
                </b-button>
              </div>
            </b-form>
          </validation-observer>
        </b-col>
      </b-col>
    <!-- /Login-->
    </b-row>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import VuexyLogo from '@core/layouts/components/Logo.vue'
import {
  BRow, BCol, BLink, BFormGroup, BFormInput, BInputGroupAppend, BInputGroup, BFormCheckbox, BCardText, BCardTitle, BImg, BForm, BButton, BAlert, VBTooltip, BFormRadioGroup, BSpinner,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import store from '@/store/index'
import { getHomeRouteForLoggedInUser } from '@/auth/utils'
import { mapGetters, mapActions } from "vuex";
import { generateDeviceId } from '@core/utils/utils'
import CodeInput from "vue-verification-code-input"

import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BLink,
    BFormGroup,
    BFormInput,
    BInputGroupAppend,
    BInputGroup,
    BFormCheckbox,
    BCardText,
    BCardTitle,
    BImg,
    BForm,
    BButton,
    BAlert,
    VuexyLogo,
    ValidationProvider,
    ValidationObserver,
    BFormRadioGroup,
    BSpinner,
    CodeInput,
  },
  mixins: [togglePasswordVisibility],
  data() {
    return {
      errorMessage:'',
      status: '',
      password: '',
      username: '',
      showOtpChannelSelection: false,
      showOtpForm: false,
      otpCode: '',
      otpChannel: 'EMAIL',
      otpChannelOptions: [
        { text: 'Email', value: 'EMAIL' },
        { text: 'SMS', value: 'SMS' }
      ],
      isLoading: false,
      deviceId: '',
      sideImg: require('@/assets/images/pages/login-v2.svg'),
      cssOptions:{
        backgroundImage: `url( ${require('@/assets/images/sarit_cover.jpg')} )`,
        backgroundSize: 'cover',
        backgroundBlendMode: 'multiply',
        backgroundColor: 'rgba(0, 0, 0,0.5)'
      },
      // validation rules
      required,
      email,
      userData: {
              id: 0,
              fullName: "",
              username: "johndoe",
              avatar: "/img/13-small.d796bffd.png",
              email: "admin@demo.com",
              role: "admin",
              ability: [
                 {
                    action: "manage",
                    subject: "all"
                 }
              ],
              extras: {
                eCommerceCartItemsCount: 5
              }
      }
    }
  },
  mounted() {
    this.deviceId = generateDeviceId()
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
    imgUrl() {
      if (store.state.appConfig.layout.skin === 'dark') {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.sideImg = require('@/assets/images/pages/login-v2-dark.svg')
        return this.sideImg
      }
      return this.sideImg
    },
  },
  methods: {
    ...mapActions(["login"]),
    handleFormSubmit() {
      this.$refs.loginForm.validate().then(success => {
        console.log('Validation result:', success)
        if (success) {
          // Move to OTP channel selection
          this.showOtpChannelSelection = true
          this.errorMessage = ''
        }
      })
    },

    backToCredentials() {
      this.showOtpChannelSelection = false
      this.errorMessage = ''
    },

    sendOTPCode() {
      this.isLoading = true
      this.errorMessage = ''

      const payload = {
        username: this.username,
        password: this.password,
        deviceId: this.deviceId,
        osVersion: navigator.userAgent,
        appVersion: '1.0',
        os: this.getOSName(),
        otpChannel: this.otpChannel
      }

      this.$store.dispatch('sendOTP', payload)
        .then(response => {
          this.isLoading = false
          this.showOtpForm = true
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'OTP Sent',
              icon: 'CheckIcon',
              variant: 'success',
              text: response.message || `OTP has been sent to your ${this.otpChannel.toLowerCase()}`,
            },
          })
        })
        .catch(error => {
          this.isLoading = false
          this.errorMessage = error.message || 'Failed to send OTP. Please try again.'
        })
    },

    verifyOTPCode() {
      if (!this.otpCode) return

      this.isLoading = true
      this.errorMessage = ''

      const payload = {
        username: this.username,
        password: this.password,
        deviceId: this.deviceId,
        osVersion: navigator.userAgent,
        appVersion: '1.0',
        os: this.getOSName(),
        otp: this.otpCode
      }

      this.$store.dispatch('verifyOTP', payload)
        .then(response => {
          this.isLoading = false

          this.userData.id = response.userId
          this.userData.fullName = response.full_names
          this.userData.username = this.username
          this.userData.email = this.username

          useJwt.setToken(response.jwttoken)
          useJwt.setRefreshToken(response.jwttoken)
          localStorage.setItem('userData', JSON.stringify(this.userData))
          this.$ability.update(this.userData.ability)

          this.$store.commit('app-ecommerce/UPDATE_CART_ITEMS_COUNT', this.userData.extras.eCommerceCartItemsCount)

          const redirectPath = localStorage.getItem('role') == "Merchant" || localStorage.getItem('role') == "Cashier"
            ? "/apps/transactions"
            : "/"

          this.$router.replace(this.$route.query.from ? this.$route.query.from : redirectPath)
            .then(() => {
              this.$toast({
                component: ToastificationContent,
                position: 'top-right',
                props: {
                  title: `Welcome ${this.userData.fullName || this.userData.username}`,
                  icon: 'CoffeeIcon',
                  variant: 'success',
                  text: `You have successfully logged in as ${this.userData.role}.`,
                },
              })
            })
        })
        .catch(error => {
          this.isLoading = false
          this.errorMessage = error.message || 'Invalid OTP. Please try again.'
        })
    },

    resendOTP() {
      this.otpCode = ''
      this.sendOTPCode()
    },

    resetForm() {
      this.showOtpChannelSelection = false
      this.showOtpForm = false
      this.otpCode = ''
      this.errorMessage = ''
      this.isLoading = false
    },

    getOSName() {
      const userAgent = navigator.userAgent.toLowerCase()
      if (userAgent.indexOf('win') > -1) return 'Windows'
      if (userAgent.indexOf('mac') > -1) return 'MacOS'
      if (userAgent.indexOf('linux') > -1) return 'Linux'
      if (userAgent.indexOf('android') > -1) return 'Android'
      if (userAgent.indexOf('iphone') > -1 || userAgent.indexOf('ipad') > -1) return 'iOS'
      return 'Web'
    },

    onOtpChange(v) {
      // Called when any digit changes
      this.otpCode = v
    },

    onOtpComplete(v) {
      // Called when all 6 digits are entered
      this.otpCode = v
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/pages/page-auth.scss';
</style>
